/**
 * Le nom qui flotte au-dessus de la tete des autres.
 *
 * En DOM, pas en 3D, et c'est le point: un nom dessine dans la scene voudrait
 * dire une texture (le projet n'en a aucune, tout est procedural) ou une
 * geometrie de lettres, et dans les deux cas des appels de dessin en plus. Une
 * poignee de `<div>` positionnes par projection ne coute rien a la carte
 * graphique, s'ecrit avec les vrais accents, et se lit dans un test.
 */
import * as THREE from 'three'
import type { EtiquetteJoueur } from '../render/joueurs.ts'
import '../styles/ui.css'

export class EtiquettesJoueurs {
  private readonly racine: HTMLDivElement
  private readonly elements = new Map<string, HTMLDivElement>()
  private readonly point = new THREE.Vector3()

  constructor(couche: HTMLElement) {
    this.racine = document.createElement('div')
    this.racine.className = 'etiquettes'
    couche.appendChild(this.racine)
  }

  /**
   * Replace toutes les etiquettes. Appele a chaque image: on reutilise les
   * elements existants, creer un `<div>` par image ferait travailler le
   * ramasse-miettes pour rien.
   */
  maj(liste: readonly EtiquetteJoueur[], camera: THREE.Camera, largeur: number, hauteur: number): void {
    const vus = new Set<string>()
    for (const e of liste) {
      vus.add(e.id)
      let el = this.elements.get(e.id)
      if (!el) {
        el = document.createElement('div')
        el.className = 'etiquette-joueur'
        el.dataset['joueur'] = e.id
        this.racine.appendChild(el)
        this.elements.set(e.id, el)
      }
      if (el.textContent !== e.nom) el.textContent = e.nom
      const couleur = `#${e.couleur.toString(16).padStart(6, '0')}`
      if (el.style.borderColor !== couleur) el.style.borderColor = couleur

      this.point.set(e.x, e.y, e.z).project(camera)
      // Derriere la camera ou hors du plan lointain: rien a montrer.
      const dedans = this.point.z <= 1 && Math.abs(this.point.x) <= 1.4 && Math.abs(this.point.y) <= 1.4
      el.style.display = dedans ? '' : 'none'
      if (!dedans) continue
      const px = (this.point.x * 0.5 + 0.5) * largeur
      const py = (-this.point.y * 0.5 + 0.5) * hauteur
      el.style.transform = `translate(-50%, -100%) translate(${Math.round(px)}px, ${Math.round(py)}px)`
    }

    for (const [id, el] of this.elements) {
      if (vus.has(id)) continue
      el.remove()
      this.elements.delete(id)
    }
  }

  detruire(): void {
    this.elements.clear()
    this.racine.remove()
  }
}
